// LoggedInUserContext.js
import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import { ModalContext } from "contexts/Modal";
import { API, Auth, Hub } from "aws-amplify";

export const Context = createContext({});

export const Provider = ({ children }) => {
  const [loggedInUserProfile, setLoggedInUserProfile] = useState({});
  const [shouldReloadLoggedInUserProfile, reloadLoggedInUserProfile] = useState(false);
  const { showModal } = useContext(ModalContext);

  // Keep latest showModal without retriggering effects
  const showModalRef = useRef(showModal);
  useEffect(() => { showModalRef.current = showModal; }, [showModal]);

  // Track whether we have an authenticated Cognito session
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (alive) setAuthed(true);
      } catch {
        if (alive) setAuthed(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        setAuthed(true);
      } else if (evt === "signOut") {
        setAuthed(false);
        setLoggedInUserProfile({});
      }
    });

    return () => { alive = false; unsub(); };
  }, []);

  // Prevent duplicate fetches (StrictMode, quick re-renders)
  const inFlight = useRef(false);

  // Fetch profile only when authenticated
  useEffect(() => {
    if (!authed) return;          // ← do nothing when logged out
    if (inFlight.current) return; // already fetching

    let alive = true;
    inFlight.current = true;

    (async () => {
      try {
        // At this point tokens/session exist
        const accountData = await API.get("AccountGatewayApi", "/profile");
        if (!alive) return;

        if (accountData?.accounts?.length === 0) {
          showModalRef.current(
            "Account Error",
            "Oops!, it looks like your user currently is not assigned to an active account."
          );
        }

        setLoggedInUserProfile(accountData);
        reloadLoggedInUserProfile(false); // reset manual reload flag
      } catch (err) {
        console.error("error fetching loggedInUserProfile", err);
        // IMPORTANT: no redirects here; App.js handles auth routing UX
      } finally {
        inFlight.current = false;
      }
    })();

    return () => { alive = false; };
  }, [authed, shouldReloadLoggedInUserProfile]);

  const isLoaded = Boolean(loggedInUserProfile && loggedInUserProfile.id);
  const profileContext = {
    loggedInUserProfile,
    reloadLoggedInUserProfile,
    isLoaded,
    isAuthed: authed,
  };

  // Always render provider so public pages can consume safely.
  // Show a tiny loader only when authed but profile not yet loaded.
  const showLoader = authed && !isLoaded;

  return (
    <Context.Provider value={profileContext}>
      {showLoader ? <div className="p-4 text-muted">Loading your profile…</div> : children}
    </Context.Provider>
  );
};

// Friendly aliases/exports
export const LoggedInUserContextProvider = Provider;
export const LoggedInUserContext = Context;
export const { Consumer } = Context;
export default Provider;
